/// <amd-module name="Build/Admin/Checks/Components/QueueChecksHub" />
import React = require("react");

import { StoresHub } from "Build/Admin/Checks/Stores/StoresHub";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { StoreStatus } from "Build/Admin/Common/Constants";

import { ChecksOverview } from "Checks/ChecksOverview/ChecksOverview";

import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";

import { Header, TitleSize } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";

import * as Resources from "Build/Admin/Checks/Resources";

export interface IQueueChecksHubProps extends IVssComponentProperties {
    storesHub: StoresHub;
}

export interface IQueueChecksHubState {
    checksDataLoaded: StoreStatus;
    agentQueue: TaskAgentQueue | undefined;
    hasManagePermission: boolean;
}

export class QueueChecksHub extends VssComponent<IQueueChecksHubProps, IQueueChecksHubState> {
    public constructor(props: IQueueChecksHubProps, context: IVssComponentContext) {
        super(props, context);

        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.storesHub.checksStore.addChangedListener(this._onChanged);
        this.props.storesHub.securityStore.addChangedListener(this._onChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.checksStore.removeChangedListener(this._onChanged);
        this.props.storesHub.securityStore.removeChangedListener(this._onChanged);
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    {this._getHeader()}
                    <div className="pipelines-environment-checks page-content page-content-top">
                        {this.state.agentQueue && this._getQueueChecksOverview()}
                    </div>
                </Page>
            </Surface>
        )
    }

    private _getQueueChecksOverview = (): JSX.Element | undefined => {
        if (this.state.agentQueue && this.state.checksDataLoaded === StoreStatus.Loaded) {
            return (
                <ChecksOverview
                    resourceName={this.state.agentQueue.name}
                    resourceType={this._queueResourceType}
                    resourceId={this.state.agentQueue.id.toString()}
                    hasManagePermission={this.state.hasManagePermission}
                    dataProviderRequestParameters={{ "queueId": this.state.agentQueue.id }} />
            );
        }

        return undefined;
    }

    private _getHeader(): JSX.Element {
        const description = this.state.agentQueue ? this.state.agentQueue.name : "";
        let environmentChecksTitle: JSX.Element = (
            <Header
                title={Resources.Checks}
                titleSize={TitleSize.Large}
                description={description}
                backButtonProps={{ onClick: this._onBackButtonClick }}
            />
        );

        return environmentChecksTitle;
    }

    private _onBackButtonClick = (): void => {
        if (this.state.agentQueue) {
            const linkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
            const queueUrl = linkingService.getAgentQueueUrl(this.state.agentQueue);
            onClickFPS(this.context.pageContext, queueUrl, true);
        }
    }

    private _onChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IQueueChecksHubState {
        return {
            checksDataLoaded: this.props.storesHub.checksStore.state.status,
            agentQueue: this.props.storesHub.selectionStore.state.selectedAgentQueue,
            hasManagePermission: this.props.storesHub.securityStore.state.hasManagePermission
        };
    }

    private _queueResourceType: string = "queue";
}
